/**
 * ConfirmPh ad slot framework: shared between the website (Google AdSense)
 * and the future Android/iOS app build (Google AdMob).
 *
 * Design goals
 *   - Subscriptions stay the primary revenue model. Ads are fallback.
 *   - One source of truth for "is this page allowed to show ads?"
 *   - Ad-free for paid business users + sensitive flows.
 *   - Default OFF unless explicit config flips it on. No accidental ads.
 *   - Same `<AdSlot placement="..." />` call works on web (today) and
 *     mobile (later): only the renderer changes per platform.
 *
 * IMPORTANT: no real ad SDK is loaded until env vars are populated:
 *   - REACT_APP_ADS_ENABLED=true          master switch
 *   - REACT_APP_WEB_ADS_ENABLED=true      website slots (AdSense)
 *   - REACT_APP_APP_ADS_ENABLED=true      mobile slots (AdMob): future
 *   - REACT_APP_ADSENSE_CLIENT_ID=ca-pub-xxxxxxxxxxxxxxxx
 *   - REACT_APP_ADSENSE_SLOT_<PLACEMENT>=<adsense unit id>
 *
 * If any of these are missing, the component renders a quiet placeholder
 * in dev (so designers can see the slot) and renders nothing in production.
 */

// Bare ENV booleans (everything is a string at runtime in CRA)
const truthy = (v) => /^(1|true|yes|on)$/i.test(String(v || ""));

export const ADS_ENABLED = truthy(process.env.REACT_APP_ADS_ENABLED);
export const WEB_ADS_ENABLED = truthy(process.env.REACT_APP_WEB_ADS_ENABLED) && ADS_ENABLED;
export const APP_ADS_ENABLED = truthy(process.env.REACT_APP_APP_ADS_ENABLED) && ADS_ENABLED;

export const ADSENSE_CLIENT_ID = process.env.REACT_APP_ADSENSE_CLIENT_ID || "";

// Pages allowed to host ads: per the brand policy. Use full path or
// path-prefix; matching is "starts with".
export const ALLOWED_AD_PATH_PREFIXES = [
  "/guides",
  "/academy",            // includes /academy/guide/<slug> and the academy index
  "/business/guides",
  "/business/academy",
  "/discover",
  "/rentals",            // legacy alias used in some routes
  "/faq",
  "/find-booking/result",
  "/request-verification/received",
];

// Pages where ads must NEVER appear: these win over the allow list.
// Match by exact path or prefix.
export const FORBIDDEN_AD_PATH_PREFIXES = [
  "/dashboard",
  "/admin",
  "/login",
  "/signup",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/verify-email",
  "/billing",
  "/pricing",
  "/checkout",
  "/confirm",            // public booking confirmation flow
  "/b/",                 // public booking pages
  "/booking/",
  "/find-booking",       // the form itself: only the /result page is allowed
  "/request-verification",
  "/contact",
  "/support",
  "/settings",
  "/account",
  "/incidents",
  "/legal",
  "/terms",
  "/privacy",
  "/safety",
  "/trust",
  "/otp",
];

/** Stable placement keys: used by AdSlot and env var lookup. */
export const PLACEMENTS = {
  guides_inline: "guides_inline",          // small banner inside a guide article
  discover_top: "discover_top",            // banner on Discover Rentals listing
  discover_inline: "discover_inline",      // small banner mid-list
  faq_bottom: "faq_bottom",                // bottom of FAQ page
  find_booking_result: "find_booking_result",
  verification_received: "verification_received",
};

/**
 * Decide whether a page is currently allowed to show ads.
 * Pass the current `location.pathname` + the current user record.
 *
 * Precedence: when a path matches both an allow-listed and a
 * forbidden prefix (e.g. `/find-booking/result` matches both
 * `/find-booking` forbidden and `/find-booking/result` allowed), the
 * **longer/more-specific** prefix wins. This lets `/find-booking/result`
 * stay ad-eligible while `/find-booking` (the form) remains blocked.
 */
export function pageAllowsAds(pathname, user) {
  if (!ADS_ENABLED) return false;
  // Paid business users get an ad-free workspace.
  if (user && hasPaidPlan(user)) return false;

  let bestAllow = -1;
  let bestForbid = -1;
  for (const prefix of ALLOWED_AD_PATH_PREFIXES) {
    if ((pathname === prefix || pathname.startsWith(prefix))
        && prefix.length > bestAllow) {
      bestAllow = prefix.length;
    }
  }
  for (const prefix of FORBIDDEN_AD_PATH_PREFIXES) {
    if ((pathname === prefix || pathname.startsWith(prefix))
        && prefix.length > bestForbid) {
      bestForbid = prefix.length;
    }
  }
  if (bestAllow > bestForbid) return true;
  if (bestForbid > -1) return false;
  // No match at all → default to deny.
  return false;
}

/** True for any logged-in user whose plan is "paid". Mirrors the
 * server-side check in `companies.plan` (`pro` or `enterprise`). */
export function hasPaidPlan(user) {
  if (!user) return false;
  const plan = (user.plan || user.company_plan || "").toLowerCase();
  return plan === "pro" || plan === "enterprise";
}

/** AdSense unit id for a placement, or "" if not configured. */
export function adsenseSlotId(placement) {
  const key = `REACT_APP_ADSENSE_SLOT_${placement.toUpperCase()}`;
  return process.env[key] || "";
}

/** Lightweight runtime self-check; used by the ad debug overlay. */
export function adsDiagnostics(pathname, user) {
  return {
    ads_enabled: ADS_ENABLED,
    web_ads_enabled: WEB_ADS_ENABLED,
    app_ads_enabled: APP_ADS_ENABLED,
    adsense_client_id_present: Boolean(ADSENSE_CLIENT_ID),
    page_allowed: pageAllowsAds(pathname, user),
    user_paid: hasPaidPlan(user),
    pathname,
  };
}
